import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../../api/client";
import MetricCard from "../../components/MetricCard";
import {
  BREAKDOWN_STATUS,
  PM_STATE,
  RECORD_TYPES,
  errorMessage,
  formatDate,
  formatDuration,
  formatHours,
  formatMoney,
  formatPercent,
  formatWhen,
  machineLabel,
  pmDueText,
} from "../assets/assetUtils";
import "../assets/Assets.css";

const inputStyle = {
  padding: "6px 10px", border: "1px solid #d6dfed", borderRadius: 7,
  fontSize: 13, color: "#374151", outline: "none", background: "#f8fafc",
};

// IST calendar dates, the same days the server reports on.
function istDateStr(offsetDays = 0) {
  return new Date(Date.now() + 5.5 * 3600000 + offsetDays * 86400000).toISOString().split("T")[0];
}

const PRESETS = [
  { label: "Today", from: 0 },
  { label: "7 days", from: -6 },
  { label: "30 days", from: -29 },
  { label: "90 days", from: -89 },
];

function Pill({ meta }) {
  return meta ? <span className={`am-pill ${meta.tone}`}>{meta.label}</span> : null;
}

function Panel({ title, tone, children }) {
  return (
    <div className="am-panel" style={tone === "bad" ? { borderColor: "#fca5a5" } : undefined}>
      <div className="am-panel-head">
        <h2 className="am-panel-title" style={tone === "bad" ? { color: "#b91c1c" } : undefined}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function AssetReportTab() {
  const today = istDateStr();
  const [from, setFrom] = useState(istDateStr(-29));
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/manufacturing/assets/report", { params: { from, to } });
      setData(res.data.data);
    } catch (err) {
      setData(null);
      setError(errorMessage(err, "Couldn't load the asset report."));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/manufacturing/assets/report/export", { params: { from, to }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asset-report-${from}${to !== from ? `-to-${to}` : ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Couldn't download the report.");
    } finally {
      setDownloading(false);
    }
  };

  const s = data?.summary;
  const v = (value) => (loading ? "—" : value);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, color: "#64748b" }}>From</label>
          <input type="date" style={inputStyle} value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          <label style={{ fontSize: 13, color: "#64748b" }}>To</label>
          <input type="date" style={inputStyle} value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
          <div className="am-period" role="group" aria-label="Quick range">
            {PRESETS.map((p) => {
              const pFrom = istDateStr(p.from);
              return (
                <button key={p.label} type="button" className={from === pFrom && to === today ? "active" : ""} onClick={() => { setFrom(pFrom); setTo(today); }}>
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
        <button className="btn-primary" style={{ fontSize: 13 }} onClick={download} disabled={downloading || loading || !data}>
          {downloading ? "Preparing…" : "Export Report"}
        </button>
      </div>

      {error && <div className="am-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div style={{ display: "flex", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
        <MetricCard label="Breakdowns" value={v(s?.breakdowns)} accent={s?.breakdowns ? "#dc2626" : undefined} />
        <MetricCard label="Downtime" value={v(formatHours(s?.downtimeHours))} accent={s?.downtimeHours ? "#dc2626" : undefined} />
        <MetricCard label="Availability" value={v(formatPercent(s?.availability))} />
        <MetricCard label="MTTR" value={v(formatHours(s?.mttrHours))} />
        <MetricCard label="MTBF" value={v(formatHours(s?.mtbfHours))} />
      </div>
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <MetricCard label="Avg. response" value={v(s?.avgResponseMinutes == null ? "—" : formatDuration(s.avgResponseMinutes))} />
        <MetricCard label="PM completed" value={v(s?.pmCompleted)} accent="#16a34a" />
        <MetricCard label="PM overdue now" value={v(s?.pmOverdue)} accent={s?.pmOverdue ? "#d97706" : undefined} />
        <MetricCard label="Maintenance cost" value={v(formatMoney(s?.maintenanceCost))} />
        <MetricCard label="Machines down now" value={v(s?.machinesDown)} accent={s?.machinesDown ? "#dc2626" : undefined} />
      </div>

      {loading ? (
        <div className="am-empty">Loading…</div>
      ) : !data ? null : (
        <>
          <Panel title="By machine">
            {data.machines.length === 0 ? (
              <p className="am-muted" style={{ margin: 0 }}>No machines yet.</p>
            ) : (
              <div className="am-table-wrap">
                <table className="am-table">
                  <thead>
                    <tr>
                      <th>Machine</th>
                      <th className="am-num">Breakdowns</th>
                      <th className="am-num">Downtime</th>
                      <th className="am-num">Availability</th>
                      <th className="am-num">MTTR</th>
                      <th className="am-num">MTBF</th>
                      <th className="am-num">PM done</th>
                      <th className="am-num">Maintenance cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.machines.map((r) => (
                      <tr key={r.machine.id}>
                        <td><Link to={`/manufacturing/assets/machines/${r.machine.id}`} className="am-strong">{machineLabel(r.machine)}</Link><div className="am-muted">{r.machine.type}</div></td>
                        <td className="am-num">{r.failures}</td>
                        <td className="am-num">{formatHours(r.downtimeHours)}</td>
                        <td className="am-num">{formatPercent(r.availability)}</td>
                        <td className="am-num">{formatHours(r.mttrHours)}</td>
                        <td className="am-num">{formatHours(r.mtbfHours)}</td>
                        <td className="am-num">{r.pmCompleted}</td>
                        <td className="am-num">{formatMoney(r.maintenanceCost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title={`Breakdowns (${data.breakdowns.length})`} tone={data.breakdowns.some((b) => b.status !== "resolved") ? "bad" : null}>
            {data.breakdowns.length === 0 ? (
              <p className="am-muted" style={{ margin: 0 }}>No machine was down in this period. ✓</p>
            ) : (
              <div className="am-table-wrap">
                <table className="am-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Machine</th>
                      <th>What happened</th>
                      <th>Stopped</th>
                      <th className="am-num">Response</th>
                      <th>Fix</th>
                      <th>Status</th>
                      <th className="am-num">Downtime</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.breakdowns.map((b) => (
                      <tr key={b.id}>
                        <td><Link to={`/manufacturing/assets/machines/${b.machine.id}?breakdown=${b.id}`} className="am-strong">{b.label}</Link></td>
                        <td>{machineLabel(b.machine)}</td>
                        <td><div className="am-clip">{b.description}</div><div className="am-muted">{b.reportedBy || "—"}</div></td>
                        <td>{formatWhen(b.startedAt)}</td>
                        <td className="am-num">{b.responseMinutes != null ? formatDuration(b.responseMinutes) : "—"}</td>
                        <td><div className="am-clip">{b.resolution || "—"}</div>{b.resolvedBy && <div className="am-muted">{b.resolvedBy}</div>}</td>
                        <td><Pill meta={BREAKDOWN_STATUS[b.status]} /></td>
                        <td className="am-num">{formatDuration(b.downtimeMinutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <div className="am-grid-2">
            <Panel title="Preventive maintenance due now">
              {data.pmAttention.length === 0 ? (
                <p className="am-muted" style={{ margin: 0 }}>Nothing is due or overdue. ✓</p>
              ) : (
                <div className="am-table-wrap">
                  <table className="am-table">
                    <tbody>
                      {data.pmAttention.map((sch) => (
                        <tr key={sch.id}>
                          <td><span className="am-strong">{sch.title}</span><div className="am-muted">{machineLabel(sch.machine)}</div></td>
                          <td>{pmDueText(sch)}</td>
                          <td><Pill meta={PM_STATE[sch.due.state]} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
            <Panel title="Warranties ending within 30 days">
              {data.warranties.length === 0 ? (
                <p className="am-muted" style={{ margin: 0 }}>None.</p>
              ) : (
                <div className="am-table-wrap">
                  <table className="am-table">
                    <tbody>
                      {data.warranties.map((w) => (
                        <tr key={w.id}>
                          <td><span className="am-strong">{machineLabel(w.machine)}</span><div className="am-muted">{w.provider}</div></td>
                          <td>{formatDate(w.endDate)}</td>
                          <td><span className={`am-pill ${w.state === "expired" ? "bad" : "warn"}`}>{w.state === "expired" ? "Expired" : "Expiring"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>

          <Panel title={`Maintenance logged (${data.records.length})`}>
            {data.records.length === 0 ? (
              <p className="am-muted" style={{ margin: 0 }}>No maintenance was logged in this period.</p>
            ) : (
              <div className="am-table-wrap">
                <table className="am-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Machine</th>
                      <th>Type</th>
                      <th>Work done</th>
                      <th>By</th>
                      <th className="am-num">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.records.map((r) => (
                      <tr key={r.id}>
                        <td>{formatWhen(r.performedAt)}</td>
                        <td>{machineLabel(r.machine)}</td>
                        <td>{RECORD_TYPES.find((t) => t.value === r.type)?.label || r.type}{r.schedule && <div className="am-muted">{r.schedule}</div>}</td>
                        <td>
                          <div className="am-clip">{r.description}</div>
                          {r.checklistName && (
                            <span className="am-muted">✓ {r.checklistName}{r.issuesFound ? <span style={{ color: "#b91c1c", fontWeight: 700 }}> · {r.issuesFound} not OK</span> : ""}</span>
                          )}
                        </td>
                        <td>{r.performedBy || "—"}</td>
                        <td className="am-num">{formatMoney(r.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
          <p className="am-muted">Availability, MTTR and MTBF are measured round the clock from each machine's installation date. Downtime counts from when a machine stopped to when its breakdown was resolved.</p>
        </>
      )}
    </div>
  );
}
